import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import type { ResourceLanguage } from 'i18next'

const STORAGE_KEY = 'lukhas_language'
const SUPPORTED_LANGUAGES = ['en', 'es', 'de', 'it', 'fr', 'zh', 'ja', 'nl'] as const

type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const localeLoaders: Record<
  SupportedLanguage,
  () => Promise<{ default: ResourceLanguage }>
> = {
  en: () => import('./locales/en'),
  es: () => import('./locales/es'),
  de: () => import('./locales/de'),
  it: () => import('./locales/it'),
  fr: () => import('./locales/fr'),
  zh: () => import('./locales/zh'),
  ja: () => import('./locales/ja'),
  nl: () => import('./locales/nl'),
}

const loadedLanguages = new Set<SupportedLanguage>()

function normalizeLanguage(language?: string | null): SupportedLanguage {
  if (language && SUPPORTED_LANGUAGES.includes(language as SupportedLanguage)) {
    return language as SupportedLanguage
  }

  return 'en'
}

function getSavedLanguage(): SupportedLanguage {
  if (typeof window !== 'undefined') {
    return normalizeLanguage(localStorage.getItem(STORAGE_KEY))
  }

  return 'en'
}

async function ensureLanguageResources(language: SupportedLanguage) {
  if (loadedLanguages.has(language)) {
    return
  }

  const module = await localeLoaders[language]()
  i18n.addResourceBundle(language, 'translation', module.default, true, true)
  loadedLanguages.add(language)
}

const initialLanguage = getSavedLanguage()

export const i18nReady = (async () => {
  const initialModule = await localeLoaders[initialLanguage]()
  loadedLanguages.add(initialLanguage)

  await i18n.use(initReactI18next).init({
    resources: {
      [initialLanguage]: { translation: initialModule.default },
    },
    lng: initialLanguage,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
  })

  const baseChangeLanguage = i18n.changeLanguage.bind(i18n)

  i18n.changeLanguage = (async (language, callback) => {
    const nextLanguage = normalizeLanguage(language)
    await ensureLanguageResources(nextLanguage)
    return baseChangeLanguage(nextLanguage, callback)
  }) as typeof i18n.changeLanguage

  if (initialLanguage !== 'en') {
    void ensureLanguageResources('en')
  }
})()

export default i18n
